import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Radio, Users, Loader2, Play, Lock, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveStreams = () => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLiveStreams();
    // Poll for new streams every 30 seconds
    const interval = setInterval(fetchLiveStreams, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveStreams = async () => {
    try {
      const response = await axios.get(`${API}/streams/live`);
      setStreams(response.data.streams || []);
    } catch (error) {
      console.error('Error fetching streams:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8 bg-[#050505]" data-testid="live-streams-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <Radio className="w-6 h-6 text-red-500 animate-pulse" />
            </div>
            <div>
              <h1 
                className="text-4xl font-normal tracking-wide uppercase text-white"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                Live Now
              </h1>
              <p className="text-gray-400">{streams.length} {streams.length === 1 ? 'stream' : 'streams'} active</p>
            </div>
          </div>

          <Button 
            onClick={() => navigate('/creator-studio')}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500/10"
          >
            <Radio className="w-4 h-4 mr-2" />
            Start Streaming
          </Button>
        </div>

        {/* Streams Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : streams.length === 0 ? (
          <div className="text-center py-20" data-testid="no-streams">
            <Radio className="w-20 h-20 text-gray-700 mx-auto mb-6" />
            <h2 className="text-2xl text-white mb-2">No Live Streams</h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              No creators are currently streaming. Check back later or start your own stream!
            </p>
            <Button 
              onClick={() => navigate('/creator-studio')}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              <Radio className="w-4 h-4 mr-2" />
              Go Live Now
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {streams.map(stream => (
              <StreamCard key={stream.id} stream={stream} />
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-red-900/30 to-orange-900/30 border border-red-500/20 rounded-lg p-8 text-center">
          <Crown className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 
            className="text-3xl font-normal tracking-wide uppercase text-white mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Become a Creator
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-6">
            Start streaming, upload videos, and build your audience. Upgrade to a Creator subscription to unlock all features.
          </p>
          <Button 
            onClick={() => navigate('/subscription')}
            className="bg-white text-black hover:bg-gray-200"
          >
            View Creator Plans
          </Button>
        </div>
      </div>
    </div>
  );
};

const StreamCard = ({ stream }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getThumbnail = () => {
    // Generate thumbnail based on platform
    if (stream.stream_url?.includes('youtube')) {
      const videoId = stream.stream_url.split('v=')[1]?.split('&')[0] || 
                      stream.stream_url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return `https://via.placeholder.com/640x360/1a1a1a/ffffff?text=LIVE`;
  };

  return (
    <Link 
      to={`/live/${stream.id}`}
      className="group block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`stream-card-${stream.id}`}
    >
      <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden hover:border-red-500/50 transition-all duration-300">
        {/* Thumbnail */}
        <div className="aspect-video relative">
          <img 
            src={getThumbnail()} 
            alt={stream.title}
            className="w-full h-full object-cover"
            onError={(e) => e.target.src = 'https://via.placeholder.com/640x360/1a1a1a/ffffff?text=LIVE'}
          />
          
          {/* Live badge */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              LIVE
            </span>
            {stream.is_premium && (
              <span className="px-2 py-1 bg-amber-500 text-black text-xs font-medium rounded">
                <Lock className="w-3 h-3 inline mr-1" />
                Premium
              </span>
            )}
          </div>

          {/* Viewer count */}
          <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/70 text-white text-xs rounded">
            <Users className="w-3 h-3" />
            {stream.viewer_count || 0}
          </div>

          {/* Play overlay on hover */}
          <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
              <Play className="w-8 h-8 text-white ml-1" />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-4">
          <h3 className="text-white font-medium line-clamp-2 mb-2 group-hover:text-red-400 transition-colors">
            {stream.title}
          </h3>
          <p className="text-gray-500 text-sm">{stream.creator_name}</p>
          <p className="text-gray-600 text-xs mt-1 capitalize">{stream.platform}</p>
        </div>
      </div>
    </Link>
  );
};

export default LiveStreams;
